import { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { InsuranceApi } from 'shared/api/insurance'
import EmptyCountryImage from 'shared/images/empty_bag.webp'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { EmptyPage } from 'shared/ui/empty-page'
import { Loader } from 'shared/ui/loader'
import { SearchInput } from 'shared/ui/search-input'

import { useInsuranceDraft } from '../../model/insurance-draft'
import { InsuranceCountry } from '../../model/types'
import { InsuranceLayout } from '../../ui/insurance-layout'

import s from './styles.module.scss'

const getCountryName = (country: InsuranceCountry, language: string) =>
  language === 'en' ? country.nameEn || country.nameRu : country.nameRu

const CountryInsurancePage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { draft, setCountry } = useInsuranceDraft()
  const [countries, setCountries] = useState<InsuranceCountry[]>([])
  const [selected, setSelected] = useState(draft.country)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const filteredCountries = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()

    return countries.filter(
      ({ nameRu, nameEn }) =>
        nameRu.toLocaleLowerCase().includes(query) ||
        nameEn.toLocaleLowerCase().includes(query),
    )
  }, [countries, search])

  useEffect(() => {
    const abortController = new AbortController()

    InsuranceApi.getCountries(abortController.signal)
      .then(({ data }) => {
        setCountries(
          data.data.map(({ id, name_ru, name_en, product_name }) => ({
            id,
            nameRu: name_ru,
            nameEn: name_en,
            productName: product_name,
          })),
        )
      })
      .catch(() => !abortController.signal.aborted && setIsError(true))
      .finally(() => !abortController.signal.aborted && setIsLoading(false))

    return () => abortController.abort()
  }, [retryKey])

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) =>
    setSearch(event.currentTarget.value)

  return (
    <InsuranceLayout
      title={t('insurance.vzr.country.title')}
      onGoBack={() => navigate(-1)}
      footer={
        !isLoading && !isError
          ? {
              disabled: !selected,
              onClick: () => {
                if (!selected) return
                setCountry(selected)
                navigate(-1)
              },
              children: t('insurance.vzr.actions.continue'),
            }
          : undefined
      }
    >
      <div className={s.content}>
        <SearchInput
          rootClassName={s.search}
          placeholder={t('insurance.vzr.country.search')}
          onChange={handleSearch}
          onReset={() => setSearch('')}
        />

        {isLoading ? (
          <Loader marginTop={100} />
        ) : isError ? (
          <EmptyPage
            title={t('insurance.vzr.errors.load_title')}
            subTitle={t('error_des')}
            buttonText={t('try_again')}
            onClick={() => {
              setIsError(false)
              setIsLoading(true)
              setRetryKey((current) => current + 1)
            }}
          />
        ) : !filteredCountries.length ? (
          <div className={s.empty}>
            <div className={s.emptyImage}>
              <img
                src={EmptyCountryImage}
                alt=""
                aria-hidden="true"
              />
            </div>
            <div className={s.emptyContent}>
              <p className="bold-22">
                {t('insurance.vzr.country.empty_title')}
              </p>
              <span className="medium-14">
                {t('insurance.vzr.country.empty_description')}
              </span>
            </div>
          </div>
        ) : (
          <div className={s.list}>
            {filteredCountries.map((country) => (
              <button
                key={country.id}
                type="button"
                className={s.option}
                aria-pressed={selected?.id === country.id}
                onClick={() => setSelected(country)}
              >
                <span className="medium-16">
                  {getCountryName(country, i18n.language)}
                </span>
                {selected?.id === country.id && (
                  <AppIcon
                    className={s.check}
                    id={AllIcons.CHECK_MARK}
                    color="var(--blue)"
                    width={24}
                    height={24}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </InsuranceLayout>
  )
}

export default CountryInsurancePage
